/* Document families as the hub stores them, and the names an operator reads.

   A family is the organising key: `sales`, `selfbilled`, `rcm` or `import_goods`. The UBL
   type codes (380, 381, 383, 389, 261) are scenarios inside a family, never a tick box.

   familyLabel("sales")          -> "Sales invoices, credit and debit notes"
   familyLabel("380")            -> "Sales invoices, credit and debit notes"  (a company saved with codes)
   familyLabel("Sales Invoice")  -> "Sales invoices, credit and debit notes"  (a company saved before codes) */

import { localizedFields, localizedRecord } from "../i18n/localized";

export type Family = "sales" | "selfbilled" | "rcm" | "import_goods";
export type TypeCode = "380" | "381" | "383" | "389" | "261";

export type FamilySpec = {
  key: Family;
  label: string;
  short: string;
  erpDoctype: "Sales Invoice" | "Purchase Invoice";
  typeCodes: TypeCode[];
  helper: string | null;
};

const FAMILY_SPECS: readonly FamilySpec[] = [
  {
    key: "sales",
    label: "Sales invoices, credit and debit notes",
    short: "Sales",
    erpDoctype: "Sales Invoice",
    typeCodes: ["380", "381", "383"],
    helper: null,
  },
  {
    key: "selfbilled",
    label: "Supplier self-billed invoices",
    short: "Self-billed",
    erpDoctype: "Purchase Invoice",
    typeCodes: ["389", "261"],
    helper: "Read from Purchase Invoices; needs the self-billed fields in ERPNext.",
  },
  {
    key: "rcm",
    label: "Reverse-charge imported services",
    short: "RCM",
    erpDoctype: "Purchase Invoice",
    typeCodes: ["380"],
    helper: "Read from Purchase Invoices; needs the reverse-charge fields and Server Script in ERPNext.",
  },
  {
    key: "import_goods",
    label: "Imported goods (customs)",
    short: "Imports",
    erpDoctype: "Purchase Invoice",
    typeCodes: ["380"],
    helper:
      "Read from Purchase Invoices; needs the import-of-goods fields and the Company customs ID in ERPNext.",
  },
];

/** Labels resolve through i18next when read (see i18n/localized.ts). */
export const FAMILIES: readonly FamilySpec[] = FAMILY_SPECS.map((spec) =>
  localizedFields(`domain.family.${spec.key}`, spec, ["label", "short", "helper"] as const),
);

export const TYPE_CODE_LABEL: Record<TypeCode, string> = localizedRecord("domain.typeCode", {
  "380": "Invoice",
  "381": "Credit note",
  "383": "Debit note",
  "389": "Self-billed invoice",
  "261": "Self-billed credit note",
});

export const INBOUND_TYPE_LABEL: Record<"380" | "381", string> = localizedRecord("domain.inboundTypeCode", {
  "380": "Supplier invoice",
  "381": "Supplier credit note",
});

/* Values a company may carry from before families existed: the UBL codes step 1
   used to post, and the display names it posted before that. */
const LEGACY_FAMILY: Record<string, Family> = {
  "380": "sales",
  "381": "sales",
  "383": "sales",
  "Sales Invoice": "sales",
  "Credit Note": "sales",
  "Debit Note": "sales",
  "389": "selfbilled",
  "261": "selfbilled",
};

function specFor(key: string): FamilySpec | undefined {
  return FAMILIES.find((spec) => spec.key === key);
}

function legacyFamily(value: string): Family | undefined {
  return specFor(value)?.key ?? LEGACY_FAMILY[value];
}

/** The family's label; a legacy code or name resolves to its family's label; anything else is returned as-is. */
export function familyLabel(key: string): string {
  const family = legacyFamily(key);
  return family ? specFor(family)!.label : key;
}

/** "Sales" | "Self-billed" | "RCM" | "Imports"; anything else is returned as-is. */
export function familyShort(key: string): string {
  return specFor(key)?.short ?? key;
}

/** The family a stored value belongs to; an unknown value is treated as sales. */
export function familyFromLegacy(value: string): Family {
  return legacyFamily(value) ?? "sales";
}

/** Maps and dedupes, keeping first-seen order; never empty, since a company always sends something. */
export function normaliseFamilies(values: string[]): Family[] {
  const out: Family[] = [];
  for (const value of values) {
    const family = familyFromLegacy(value);
    if (!out.includes(family)) out.push(family);
  }
  return out.length ? out : ["sales"];
}

/** The operator-facing name of a type code; received documents read from the supplier's side. */
export function typeCodeLabel(code: string | null | undefined, direction: "in" | "out" = "out"): string {
  if (code == null) return "—";
  if (direction === "in") return (INBOUND_TYPE_LABEL as Record<string, string>)[code] ?? "—";
  return (TYPE_CODE_LABEL as Record<string, string>)[code] ?? "—";
}

/** The ERPNext doctypes the hub polls for these families, in the order given, each once. */
export function erpDoctypesFor(families: string[]): string[] {
  const out: string[] = [];
  for (const value of families) {
    const doctype = specFor(familyFromLegacy(value))!.erpDoctype;
    if (!out.includes(doctype)) out.push(doctype);
  }
  return out;
}
